import React, { useEffect, useState } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import "../sda/styles.scss";
import "./styles.scss";
import { cardBackgroundColor } from "../../config/palette";
import { Text } from "@amzn/stencil-react-components/text";
import { Link } from "@amzn/stencil-react-components/link";
import calendar from "./calendar.png";
import { TriggerButton } from "@amzn/stencil-react-components/helpers";
import { ExpanderContent } from "@amzn/stencil-react-components/expander";
import { Button } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { useNavigatorWithURLReady } from "../../hooks/use-navigator";
import { processTime } from "../../utility/time-processor";
import useArb from "../../hooks/use-arb";
import { getSettings, MX_STAGES } from "../../helpers/get-settings";
import { getMessageValues } from "../../config/task-message-config";

interface PreHireAppointmentCardHeaderProps {
  nheDate?: string;
  nheTimeRange?: string;
  appointmentSelfServiceURL: string;
  onViewDetailsClick: () => void;
  onViewListOfDocsClick: () => void;
  eligibleForAppointmentRescheduling: boolean;
  supportedLanguage: string;
}

export const PreHireAppointmentCardHeader = ({
  nheDate,
  nheTimeRange,
  appointmentSelfServiceURL,
  eligibleForAppointmentRescheduling,
  onViewDetailsClick,
  onViewListOfDocsClick,
  supportedLanguage,
}: PreHireAppointmentCardHeaderProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const startTime = nheTimeRange?.split("-")[0];
  const processedStartTime = processTime(startTime);
  //reason why ASH has to process time and date is because NHE doesnt handle the format by locale. we will need to format it into a valid date object to be translated by panther
  const startDateAndTime = nheDate?.toString() + " " + processedStartTime;
  const dateWithTime = new Date(startDateAndTime);
  const bundle = useArb("HVHChecklistPantherResource");
  const monthFormatted = bundle.formatMessage(
    "Checklist-AppointmentCard-MonthUpperCase",
    {
      dateParam: dateWithTime,
    }
  );
  const dayFormatted = bundle.formatMessage("Checklist-AppointmentCard-Day", {
    dateParam: dateWithTime,
  });

  const formattedAppointmentMsg = bundle.formatMessage(
    "Checklist-AppointmentCard-Date",
    {
      dateParam: dateWithTime,
    }
  );
  const formattedAppointmentSupportedLanguageMsg =
    bundle.getMessage("Checklist-AppointmentCard-Language") +
    ": " +
    supportedLanguage;

  const viewButton = bundle.getMessage("Checklist-AppointmentCard-ButtonText1");
  const listOfDoc = bundle.getMessage("Checklist-AppointmentCard-Documents");
  const documentLink = (
    <Link
      target="_blank"
      href={getMessageValues().acceptableDocLink}
      onClick={onViewListOfDocsClick}
    >
      {listOfDoc}
    </Link>
  );
  const expandedText1 = bundle.getMessage("Checklist-AppointmentCard-Details1");
  const expandedText2 = bundle.formatMessage(
    "Checklist-AppointmentCard-Details2",
    {
      documentLink: documentLink,
    }
  );
  const reschedule = bundle.getMessage("Checklist-AppointmentCard-ButtonText2");

  const [isMexico, setMexicoFlag] = useState<boolean>();
  useEffect(() => {
    getSettings().then((settings) =>
      setMexicoFlag(MX_STAGES.includes(settings.stage))
    );
  });

  return (
    <Card justifyContent="center" padding="0">
      <Col width="100%">
        <Row
          backgroundColor={cardBackgroundColor}
          justifyContent="center"
          alignItems="center"
          gridGap="1rem"
          padding={"1rem"}
        >
          <Col justifyContent="center">
            <img
              src={calendar}
              alt="Calendar"
              width="80px"
              aria-hidden="true"
            />
            <Text className="calendar-day" fontSize="35px">
              {dayFormatted}
            </Text>
            <Text className="calendar-month" fontSize="12px">
              {monthFormatted}
            </Text>
          </Col>

          <Col maxWidth={220}>
            <Text fontSize="T300" lineHeight="2">
              {formattedAppointmentMsg}
            </Text>
            {!isMexico && (
              <Text fontSize="T300" lineHeight="2">
                {formattedAppointmentSupportedLanguageMsg}
              </Text>
            )}
          </Col>

          <Col>
            <TriggerButton
              iconAltText="Information about appointment"
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </Col>
        </Row>
        <ExpanderContent isExpanded={isExpanded}>
          <Col gridGap="1rem" padding="1rem">
            <Row justifyContent="center" alignItems="center">
              <Col maxWidth={368} gridGap="1rem">
                <Row>
                  <Text fontSize="T200" lineHeight="1.5" textAlign="justify">
                    {expandedText1}
                  </Text>
                </Row>
                <Row>
                  <Text fontSize="T200" lineHeight="1.5" textAlign="justify">
                    {expandedText2}
                  </Text>
                </Row>

                <Row gridGap={"1rem"} justifyContent={"center"}>
                  <Button onClick={onViewDetailsClick}>{viewButton}</Button>
                  {eligibleForAppointmentRescheduling && (
                    <Button
                      onClick={useNavigatorWithURLReady(
                        appointmentSelfServiceURL
                      )}
                    >
                      {reschedule}
                    </Button>
                  )}
                </Row>
              </Col>
            </Row>
          </Col>
        </ExpanderContent>
      </Col>
    </Card>
  );
};
